import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, type SupportRequestRow } from "@/services/adminApi";
import { SupportTriageTabs } from "@/components/support/SupportTriageTabs";

const STATUSES = ["OPEN", "TRIAGED", "LINKED_TO_ISSUE", "RESOLVED", "CLOSED"] as const;
const TYPES = ["BUG", "AI_RESPONSE", "FEATURE_REQUEST", "BILLING_ACCOUNT", "GENERAL_FEEDBACK"] as const;
const SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"] as const;

const STATUS_META: Record<
  string,
  { label: string; dot: string; chip: string }
> = {
  OPEN: { label: "Open", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 ring-amber-200/60" },
  TRIAGED: { label: "Triaged", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700 ring-sky-200/60" },
  LINKED_TO_ISSUE: { label: "Linked", dot: "bg-violet-500", chip: "bg-violet-50 text-violet-700 ring-violet-200/60" },
  RESOLVED: { label: "Resolved", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/60" },
  CLOSED: { label: "Closed", dot: "bg-zinc-400", chip: "bg-zinc-50 text-zinc-600 ring-zinc-200/60" },
};

const SEVERITY_META: Record<string, { label: string; color: string }> = {
  LOW: { label: "Low", color: "text-zinc-500" },
  MEDIUM: { label: "Medium", color: "text-amber-600" },
  HIGH: { label: "High", color: "text-orange-600" },
  CRITICAL: { label: "Critical", color: "text-red-600" },
};

const TYPE_LABEL: Record<string, string> = {
  BUG: "Bug",
  AI_RESPONSE: "Bad AI answer",
  FEATURE_REQUEST: "Feature request",
  BILLING_ACCOUNT: "Billing & account",
  GENERAL_FEEDBACK: "General feedback",
};

const formatTitle = (s: string) => s.replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());

export default function SupportRequestsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SupportRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", type: "", severity: "" });
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;
      if (filters.severity) params.severity = filters.severity;
      const { requests } = await adminApi.getSupportRequests(params);
      setRows(requests);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.user?.email?.toLowerCase().includes(q) ||
        r.organization?.name?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const counts = useMemo(() => {
    const needsReply = rows.filter((r) => r.needsReply).length;
    const open = rows.filter((r) => r.status === "OPEN").length;
    const total = rows.length;
    return { needsReply, open, total };
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      <div className="mx-auto max-w-6xl px-8 py-10">
        {/* Header */}
        <header className="mb-8 flex items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-500">
              <span className="inline-flex h-1 w-1 rounded-full bg-orange-500" />
              Internal triage
            </div>
            <h1 className="mt-2 text-[28px] font-semibold tracking-[-0.02em] text-zinc-900">
              Support requests
            </h1>
            <p className="mt-1 text-[13.5px] text-zinc-500">
              Conversations from customers, sorted by latest activity.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Stat label="Needs reply" value={counts.needsReply} accent />
            <Stat label="Open" value={counts.open} />
            <Stat label="Total" value={counts.total} />
          </div>
        </header>

        <SupportTriageTabs />

        {/* Filter bar */}
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-200/80 bg-white p-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
          <div className="flex flex-1 items-center gap-2 px-3">
            <svg className="h-4 w-4 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, email, or organization…"
              className="w-full bg-transparent text-[13.5px] text-zinc-800 placeholder:text-zinc-400 outline-none"
            />
          </div>
          <div className="flex items-center gap-1.5 border-l border-zinc-200/80 pl-3">
            <FilterChips
              label="Status"
              options={STATUSES.map((s) => ({ value: s, label: STATUS_META[s].label }))}
              value={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            />
            <FilterChips
              label="Type"
              options={TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
              value={filters.type}
              onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
            />
            <FilterChips
              label="Severity"
              options={SEVERITIES.map((s) => ({ value: s, label: SEVERITY_META[s].label }))}
              value={filters.severity}
              onChange={(v) => setFilters((f) => ({ ...f, severity: v }))}
            />
            {(filters.status || filters.type || filters.severity) && (
              <button
                onClick={() => setFilters({ status: "", type: "", severity: "" })}
                className="ml-1 rounded-md px-2 py-1 text-[12px] font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-zinc-100/70" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-20">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-50 text-zinc-400">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5h11A2.5 2.5 0 0 1 20 7.5v6A2.5 2.5 0 0 1 17.5 16h-7.7l-3.4 2.6a.6.6 0 0 1-.9-.5V16h-.5A2.5 2.5 0 0 1 2.5 13.5v-6Z" />
                </svg>
              </span>
              <p className="text-[13.5px] text-zinc-500">No support requests match your filters.</p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {filtered.map((r) => {
                const status = STATUS_META[r.status] ?? STATUS_META.CLOSED;
                const severity = SEVERITY_META[r.severity] ?? SEVERITY_META.LOW;
                return (
                  <li key={r.id}>
                    <button
                      onClick={() => navigate(`/admin/support/${r.id}`)}
                      className="group flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-[#fafaf9]"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-zinc-50 text-zinc-500 ring-1 ring-zinc-200/60">
                        <TypeIcon type={r.type} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {r.needsReply && (
                            <span className="relative flex h-2 w-2 shrink-0" title="Awaiting internal reply">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-500 opacity-50" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500" />
                            </span>
                          )}
                          <h3 className="truncate text-[14px] font-medium tracking-[-0.005em] text-zinc-900">
                            {r.title}
                          </h3>
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-[12px] text-zinc-500">
                          <span className="truncate">{r.user?.email ?? "—"}</span>
                          {r.organization?.name && (
                            <>
                              <span className="text-zinc-300">·</span>
                              <span className="truncate">{r.organization.name}</span>
                            </>
                          )}
                          <span className="text-zinc-300">·</span>
                          <span className="tabular-nums">{formatRelative(r.lastMessageAt)}</span>
                        </div>
                      </div>
                      <div className="hidden items-center gap-2 sm:flex">
                        <span className={`inline-flex items-center gap-1 text-[11.5px] font-medium ${severity.color}`}>
                          <span className="h-1 w-1 rounded-full bg-current" />
                          {severity.label}
                        </span>
                        <span className="text-zinc-300">·</span>
                        <span className="text-[11.5px] text-zinc-500">{TYPE_LABEL[r.type] ?? formatTitle(r.type)}</span>
                      </div>
                      <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${status.chip}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                        {status.label}
                      </span>
                      <svg className="h-4 w-4 shrink-0 text-zinc-300 transition-transform group-hover:translate-x-0.5 group-hover:text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 6 6 6-6 6" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`flex items-baseline gap-2 rounded-xl px-3.5 py-2 ring-1 ring-inset ${accent && value > 0 ? "bg-orange-50 ring-orange-200/70" : "bg-white ring-zinc-200/70"}`}>
      <span className={`text-[18px] font-semibold tabular-nums tracking-tight ${accent && value > 0 ? "text-orange-700" : "text-zinc-900"}`}>{value}</span>
      <span className={`text-[11.5px] font-medium ${accent && value > 0 ? "text-orange-700/80" : "text-zinc-500"}`}>{label}</span>
    </div>
  );
}

function FilterChips({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <div className="relative">
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer appearance-none rounded-md bg-transparent py-1.5 pl-2.5 pr-7 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-100 focus:outline-none"
      >
        <option value="">{label}: All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {label}: {o.label}
          </option>
        ))}
      </select>
      <svg className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="m6 9 6 6 6-6" />
      </svg>
      {current && (
        <span className="pointer-events-none absolute left-0 top-0 -mt-1 ml-1 h-1.5 w-1.5 rounded-full bg-orange-500" />
      )}
    </div>
  );
}

function TypeIcon({ type }: { type: string }) {
  // Minimal Lucide-style 1.8 stroke icons.
  switch (type) {
    case "BUG":
      return (
        <svg className="h-[15px] w-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 2v2M16 2v2" />
          <rect x="8" y="6" width="8" height="14" rx="4" />
          <path d="M12 14v6M3 10h4M17 10h4M3 18h4M17 18h4M3 6l3 2M21 6l-3 2" />
        </svg>
      );
    case "AI_RESPONSE":
      return (
        <svg className="h-[15px] w-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v3M12 18v3M5.6 5.6l2 2M16.4 16.4l2 2M3 12h3M18 12h3M5.6 18.4l2-2M16.4 7.6l2-2" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "FEATURE_REQUEST":
      return (
        <svg className="h-[15px] w-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a7 7 0 0 0-4 12.7V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.3A7 7 0 0 0 12 2Z" />
          <path d="M10 22h4" />
        </svg>
      );
    case "BILLING_ACCOUNT":
      return (
        <svg className="h-[15px] w-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2.5" />
          <path d="M2 10h20M6 15h4" />
        </svg>
      );
    case "GENERAL_FEEDBACK":
    default:
      return (
        <svg className="h-[15px] w-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6.6A2.6 2.6 0 0 1 6.6 4h10.8A2.6 2.6 0 0 1 20 6.6v7.2a2.6 2.6 0 0 1-2.6 2.6h-5.7l-3.5 2.7a.55.55 0 0 1-.88-.44V16.4H6.6A2.6 2.6 0 0 1 4 13.8V6.6Z" />
        </svg>
      );
  }
}

function formatRelative(dateStr: string): string {
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}
